import { MessageCircle, MapPin, Clock, Phone, Facebook, Share2 } from "lucide-react";
import { VeveMotif } from "../components/VeveMotif";
import { RetreatForm } from "../components/RetreatForm";

const WA_HAITI = "https://wa.me/50938432794";
const WA_CANADA = "https://wa.me/14389235961";

const WhatsAppBtn = ({ href, label, testid }) => (
  <a href={href} target="_blank" rel="noopener noreferrer"
    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#25D366] text-white text-sm font-medium hover:bg-[#1DA851] transition-colors duration-200"
    data-testid={testid}>
    <MessageCircle size={16} /> {label}
  </a>
);

const ShareRow = ({ retreat }) => {
  const url = `${window.location.origin}/retraites`;
  const text = `Retraite ${retreat.country} — ${retreat.place} · Fondation Sèlide Kouzen-Kouzin (Chimen Nanm) : ${url}`;
  return (
    <div className="flex items-center gap-3 mt-5 pt-4 border-t border-primary/10" data-testid={`share-row-${retreat.id}`}>
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Share2 size={13} /> Partager :</span>
      <a href={`https://wa.me/?text=${encodeURIComponent(text)}`} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-[#1DA851] hover:underline underline-offset-4"
        data-testid={`share-whatsapp-${retreat.id}`}>
        <MessageCircle size={14} /> WhatsApp
      </a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`}
        target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-xs text-[#1877F2] hover:underline underline-offset-4"
        data-testid={`share-facebook-${retreat.id}`}>
        <Facebook size={14} /> Facebook
      </a>
    </div>
  );
};

const RETREATS = [
  {
    id: "benin",
    country: "Bénin",
    place: "Vallée de l'Ouémé — Couvent Dah Ayidji",
    duration: "Programme initiatique de 22 jours",
    description:
      "Retraite initiatique au cœur de la tradition Vodoun béninoise, au Couvent Dah Ayidji, sous l'autorité de la Reine-Mère des Divinités. Immersion complète : enseignements, rites, rencontre des autorités traditionnelles et retour aux sources du Vodou.",
    image: null,
    contacts: [{ href: WA_CANADA, label: "WhatsApp — Manbo Sèlide (+1 438 923 5961)", testid: "wa-benin-canada" }],
  },
  {
    id: "haiti",
    country: "Haïti",
    place: "Lascahobas — Lakou Impérial Savann Karé",
    duration: "Séjours et retraites au lakou",
    description:
      "Séjour au Lakou Impérial Savann Karé, espace communautaire traditionnel soutenu par la Fondation depuis 2021. Vie du lakou, cérémonies selon le calendrier culturel, transmission directe sous le regard de Dutty Boukman.",
    image: "/lakou/lakou4.jpg",
    reference: "Référence en Haïti : Anperè Roobens Thélémaque — +509 3843 2794",
    contacts: [
      { href: WA_HAITI, label: "WhatsApp — Anperè Roobens Thélémaque (+509 3843 2794)", testid: "wa-haiti" },
      { href: WA_CANADA, label: "WhatsApp — Manbo Sèlide (+1 438 923 5961)", testid: "wa-haiti-canada" },
    ],
  },
  {
    id: "senegal",
    country: "Sénégal",
    place: "Dakar — Voyages initiatiques et lieux de mémoire",
    duration: "Circuit initiatique : Île de Gorée, Saint-Louis, etc.",
    description:
      "Voyage initiatique sur les lieux de mémoire de la traite transatlantique : la Maison des Esclaves de l'Île de Gorée, Saint-Louis et les sites historiques du Sénégal. Un pèlerinage de reconnexion, de recueillement et de transmission.",
    image: "/retraites/goree.jpg",
    contacts: [{ href: WA_CANADA, label: "WhatsApp — Manbo Sèlide (+1 438 923 5961)", testid: "wa-senegal-canada" }],
  },
];

export default function RetreatsPage() {
  return (
    <main className="max-w-6xl mx-auto px-6 py-16 relative z-10" data-testid="retreats-page">
      <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">Fondation Sèlide Kouzen-Kouzin</p>
      <h1 className="text-4xl sm:text-5xl font-serif-display tracking-tight">Retraites initiatiques et voyages mémoriels</h1>
      <p className="text-foreground/70 mt-4 max-w-2xl leading-relaxed">
        Trois destinations, un même chemin : retourner aux sources, honorer la mémoire et recevoir la transmission dans les lieux vivants de la tradition.
      </p>

      <div className="space-y-10 mt-14">
        {RETREATS.map((r, i) => (
          <article key={r.id} className="card-lift bg-card border border-primary/15 rounded-lg overflow-hidden grid grid-cols-1 md:grid-cols-5" data-testid={`retreat-${r.id}`}>
            <div className={`relative md:col-span-2 min-h-[240px] ${i % 2 === 1 ? "md:order-last" : ""}`}>
              {r.image ? (
                <img src={r.image} alt={r.place} className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-primary to-secondary flex items-center justify-center overflow-hidden">
                  <VeveMotif className="w-72 h-72" opacity={0.7} color="#FFFFFF" />
                </div>
              )}
              <span className="absolute top-4 left-4 text-xs uppercase tracking-widest bg-background/90 text-primary px-4 py-1.5 rounded-full font-medium">{r.country}</span>
            </div>
            <div className="md:col-span-3 p-8">
              <h2 className="text-2xl font-serif-display flex items-start gap-2"><MapPin size={20} className="text-primary mt-1.5 shrink-0" /> {r.place}</h2>
              <p className="text-sm text-secondary font-medium mt-2 flex items-center gap-2"><Clock size={14} /> {r.duration}</p>
              <p className="text-sm text-foreground/70 mt-4 leading-relaxed">{r.description}</p>
              {r.reference && (
                <p className="text-sm text-foreground mt-4 flex items-center gap-2 font-medium" data-testid="haiti-reference">
                  <Phone size={14} className="text-primary" /> {r.reference}
                </p>
              )}
              <div className="flex flex-wrap gap-3 mt-6">
                {r.contacts.map((c) => (
                  <WhatsAppBtn key={c.testid} {...c} />
                ))}
              </div>
              <ShareRow retreat={r} />
            </div>
          </article>
        ))}
      </div>

      <div className="mt-16" id="preinscription">
        <RetreatForm />
      </div>

      <div className="mt-16 border border-dashed border-primary/25 rounded-lg p-8 text-center" data-testid="retreats-levels-note">
        <p className="font-serif-display text-xl text-foreground/80">Niveaux 2 (Approfondissement — Automne 2026) et 3 (Transmission + stage pratique — Printemps 2027) à venir</p>
        <p className="text-sm text-muted-foreground mt-2">Les retraites font partie intégrante du parcours Chimen Nanm : le stage pratique du Niveau 3 se déroule sur ces lieux de tradition.</p>
      </div>
    </main>
  );
}
